import { Component, DestroyRef, inject, OnInit, signal, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ServerRepresentedEvent } from '@app/modules/events/models/event.model'
import { EventService } from '@app/modules/events/services/event.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { environment } from '@environments/environment'
import { FullCalendarComponent, FullCalendarModule } from '@fullcalendar/angular'
import { CalendarOptions, EventClickArg, EventInput } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import { record } from '../../interfaces/metric.interface'
import { MetricsService } from '../../services/metrics.service'
import { SupportRequestModalComponent } from '../support-request-modal/support-request-modal.component'

/**
 * Events Calendar Component
 * Displays live and upcoming events in a calendar view for the current month
 */
@Component({
  selector: 'ngx-events-calendar',
  templateUrl: './events-calendar.component.html',
  styleUrls: ['./events-calendar.component.scss'],
  standalone: true,
  imports: [FullCalendarModule, SupportRequestModalComponent],
  providers: [EventService]
})
export class EventsCalendarComponent implements OnInit {
  @ViewChild('calendar') private _calendarComponent!: FullCalendarComponent
  private readonly _destroyRef = inject(DestroyRef)
  private _allEvents: record[] = []
  private _currentDate = signal(new Date())
  public loading = signal(false)
  public selectedEvent = signal<Partial<ServerRepresentedEvent> | null>(null)
  public calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    headerToolbar: false, // Hide built-in toolbar - we have custom controls
    events: [],
    eventClick: this._handleEventClick.bind(this),
    height: 'auto',
    eventDisplay: 'block',
    displayEventTime: false,
    dayMaxEvents: 3,
    moreLinkText: 'more',
    eventClassNames: (arg) => {
      return arg.event.extendedProps['status'] === 'Live' ? ['live-event'] : ['upcoming-event']
    }
  }

  constructor(private _metricsService: MetricsService, private _modalService: ModalService, private _eventService: EventService) {}

  ngOnInit() {
    this._loadEventsForCurrentMonth()
  }

  /**
   * Loads events for the currently displayed month
   * Only fetches events that are live or upcoming and overlap with the selected month
   */
  private _loadEventsForCurrentMonth(): void {
    const currentMonth = this._currentDate().getMonth()
    const currentYear = this._currentDate().getFullYear()
    this.loading.set(true)

    // Calculate month boundaries
    const monthStart = new Date(currentYear, currentMonth, 1)
    const monthEnd = new Date(currentYear, currentMonth + 1, 0, 23, 59, 59)

    // Use the optimized statistics API which now includes event IDs
    this._metricsService
      .totalLiveAndPlannedEventsByDate(this._currentDate().toISOString().split('T')[0])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          const events = response.data || []

          // Filter for events that overlap with the current calendar month
          const filteredEvents = events.filter((event) => {
            const eventStart = new Date(event.start_date)
            const eventEnd = new Date(event.end_date)
            const excludedWorkspaces = ['dmr', 'micetribe-design', 'mudathernasricl', 'mice-integ']

            // Check if event overlaps with the displayed month
            return (
              ((eventStart >= monthStart && eventStart <= monthEnd) || // Starts in this month
                (eventEnd >= monthStart && eventEnd <= monthEnd) || // Ends in this month
                (eventStart <= monthStart && eventEnd >= monthEnd)) && // Spans entire month
              (!environment.production || !excludedWorkspaces.includes(event.workspace_slug))
            )
          })

          this._allEvents = filteredEvents
          this._updateCalendarEvents()
          this.loading.set(false)
        },
        error: () => {
          this.loading.set(false)
        }
      })
  }

  /**
   * Updates the calendar with events
   * Events are already filtered by loadEventsForCurrentMonth, so we just display all of them
   */
  private _updateCalendarEvents(): void {
    // Convert to FullCalendar event format
    const calendarEvents: EventInput[] = this._allEvents.map((event) => ({
      id: event.id?.toString() || event.event_slug,
      title: event.event_name,
      start: event.start_date,
      end: event.end_date,
      extendedProps: {
        eventRecord: event,
        status: event.status,
        workspaceName: event.workspace_name,
        slug: event.event_slug
      },
      backgroundColor: event.status === 'Live' ? '#10b981' : '#3b82f6',
      borderColor: event.status === 'Live' ? '#059669' : '#2563eb'
    }))

    // Update calendar options with new events AND current date
    // This ensures the calendar stays on the correct month when re-rendering
    this.calendarOptions = {
      ...this.calendarOptions,
      initialDate: this._currentDate(),
      events: calendarEvents
    }
  }

  /**
   * Handles event click to open support request modal
   * Fetches the full event details using the event ID from the statistics data
   */
  private _handleEventClick(clickInfo: EventClickArg): void {
    if (this.selectedEvent()) return
    this.selectedEvent.set({})
    const eventRecord = clickInfo.event.extendedProps['eventRecord'] as record
    if (eventRecord && eventRecord.id) {
      // Fetch full event details using the ID from statistics API
      // Note: We don't set loading state here - that's only for calendar data loading
      this._eventService
        .getEventById(eventRecord.id)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (fullEvent: Partial<ServerRepresentedEvent>) => {
            // Merge with the record data to ensure we have all needed fields
            const completeEvent: ServerRepresentedEvent = {
              ...fullEvent,
              id: eventRecord.id,
              name: eventRecord.event_name,
              slug: eventRecord.event_slug,
              public_start_date: eventRecord.start_date,
              public_end_date: eventRecord.end_date,
              workspace: {
                name: eventRecord.workspace_name,
                slug: eventRecord.workspace_slug,
                ...fullEvent.workspace
              }
            } as ServerRepresentedEvent

            this.selectedEvent.set(completeEvent)
            this._modalService.toggleModal('support-request-modal')
          },
          error: (err) => {
            this.selectedEvent.set(null)
            console.error('Error loading full event details:', err)
          }
        })
    } else {
      this.selectedEvent.set(null)
      console.error('Event ID not found for clicked event.')
    }
  }

  /**
   * Navigate to previous month
   */
  public previousMonth(): void {
    const calendarApi = this._calendarComponent?.getApi()
    if (!calendarApi) return

    // Calculate the new date (previous month)
    const current = this._currentDate()
    const newDate = new Date(current.getFullYear(), current.getMonth() - 1, 1)

    // Update our signal
    this._currentDate.set(newDate)

    // Navigate calendar to new date
    calendarApi.gotoDate(newDate)

    // Reload events for the new month
    this._loadEventsForCurrentMonth()
  }

  /**
   * Navigate to next month
   */
  public nextMonth(): void {
    const calendarApi = this._calendarComponent?.getApi()
    if (!calendarApi) return

    // Calculate the new date (next month)
    const current = this._currentDate()
    const newDate = new Date(current.getFullYear(), current.getMonth() + 1, 1)

    // Update our signal
    this._currentDate.set(newDate)

    // Navigate calendar to new date
    calendarApi.gotoDate(newDate)

    // Reload events for the new month
    this._loadEventsForCurrentMonth()
  }

  /**
   * Navigate to today
   */
  public goToToday(): void {
    const calendarApi = this._calendarComponent?.getApi()
    if (calendarApi) {
      const today = new Date()

      // Update our signal
      this._currentDate.set(today)

      // Navigate calendar to today
      calendarApi.gotoDate(today)

      this._loadEventsForCurrentMonth()
    }
  }

  /**
   * Get month and year display text
   */
  public getMonthYearText(): string {
    const date = this._currentDate()
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
  }

  /**
   * Close the support request modal
   */
  public onModalClose(): void {
    this.selectedEvent.set(null)
  }
}
